import React, { useCallback, useEffect, useMemo } from 'react'
import { useModal } from '../../../components/useModal'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import TextInput from '../../../components/FormElements/TextInput'
import { Button } from '@demandlocal/components'
import { useTable } from 'react-table'
import SelectInput from '../../../components/FormElements/SelectInput'
import { omit, startCase } from 'lodash'
import GhostButton from '../../../components/Buttons/GhostButton'
import { toast } from 'react-hot-toast'
import { useExportToXLS } from '../../../lib/hooks/useExportToXLS'
import { UseMutationResult } from 'react-query'
import { useInView } from 'react-intersection-observer'
import { motion } from 'framer-motion'
import { findBestMatch } from 'string-similarity'
import CustomersUploadColumnsMapper from '../../../components/CustomersUpload/ColumnsMapper'

type CSVData = {
  data: Array<Record<string, unknown>>
}

type Props = {
  data: CSVData
  ftpConnection: {
    ftp_url: string
    ftp_username: string
    ftp_password: string
    fetch_frequency?: { value: string }
    fetch_time?: { value: string }
    fetch_day?: { value: string }
    timezone?: { value: string }
  }
  ftpUploadCSVMutation: UseMutationResult<unknown>
  databaseColumns?: Array<string>
}

const FTPFileMapper = (props: Props): JSX.Element => {
  const { data, ftpConnection, ftpUploadCSVMutation, databaseColumns } = props
  const { closeModal, closeAllModals } = useModal()
  const { exportToCSV } = useExportToXLS()

  const [ref, inView, entry] = useInView({
    threshold: 1,
    triggerOnce: true,
    fallbackInView: true,
  })

  const rowsOptions = useMemo(() => {
    return (
      databaseColumns?.map((column) => {
        return {
          label: startCase(column),
          value: column,
        }
      }) ?? []
    )
  }, [databaseColumns])

  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    control,
    handleSubmit,
    setError,
    getValues,
    setValue,
    formState: { isSubmitting, errors },
  } = formContext

  const columns = useMemo(() => {
    return Object.keys(data.data[0]).map((key) => {
      return {
        accessor: key,
        Header: key,
        columns: [{ accessor: key, Header: key }],
      }
    })
  }, [data])

  const tableInstance = useTable({ columns, data: data.data.slice(0, 200) })
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance

  const isEmailMapped = useCallback((formValues) => {
    return (
      Object.entries(formValues)
        .filter(([key, value]) => value)
        // @ts-ignore
        .map(([key, value]) => value?.value)
        ?.includes('email')
    )
  }, [])
  useEffect(() => {
    if (databaseColumns) {
      Object.keys(data.data[0]).map((key) => {
        const { bestMatch, bestMatchIndex } = findBestMatch(
          key,
          databaseColumns.map((column) => column.split('_').join(' ')),
        )
        const hasValue = getValues(key)
        if (bestMatch.rating >= 0.5 && !hasValue) {
          if (key.toLocaleLowerCase().includes('email'))
            if (databaseColumns[bestMatchIndex] !== 'email') return
          setValue(key, {
            label: startCase(databaseColumns[bestMatchIndex]),
            value: databaseColumns[bestMatchIndex],
          })
        }
      })
    }
  }, [data.data, getValues, databaseColumns, setValue])
  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    await new Promise<void>((resolve, reject) => {
      if (!isEmailMapped(values)) {
        setError('email', { type: 'custom', message: '' })
        reject(new Error('Email is not mapped'))
        return
      }

      const {
        source_type: { value: sourceType },
      } = values

      const rows = omit(values, 'source_name')
      const rowsMapping = Object.entries(rows)
        .filter(([key, value]) => value)
        .map(([key, value]) => {
          return { [key]: value.value }
        })
      ftpUploadCSVMutation.mutate(
        {
          data: {
            url: ftpConnection.ftp_url,
            username: ftpConnection.ftp_username,
            password: ftpConnection.ftp_password,
            saas_data_provider_id: 1,
            customer_source: values.source_name,
            source_type: sourceType,
            config: {
              rows_mapping: rowsMapping,
              fetch_frequency: ftpConnection?.fetch_frequency?.value,
              fetch_time: ftpConnection?.fetch_time?.value,
              fetch_day: ftpConnection?.fetch_day?.value,
              fetch_timezone: ftpConnection?.timezone,
            },
          },
        },
        {
          onSuccess: () => {
            closeAllModals()
            toast.success('Customers successfully uploaded!')
            resolve()
          },
          onError: (error) => {
            reject(error)
          },
        },
      )
    })
  }
  return (
    <FormProvider {...formContext}>
      <form
        ref={ref}
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col px-12 pt-12 font-Spartan h-full'}>
        <div className={'flex flex-1 flex-col  overflow-hidden'}>
          <div className={'font-bold text-[28px] text-primary-xiketic'}>
            Import FTP data
          </div>
          <div className={'pt-8 pb-8 border-b-2'}>
            <div className={'w-1/2'}>
              <TextInput
                rules={{ required: 'This field is required' }}
                inverted
                placeholder={'Ex. FTP-download-import_Mar2022'}
                name={'source_name'}
                label={'Name your source'}
                control={control}
                info={'Give it a clear name so you remember it easily'}
              />
            </div>
            <div className={'w-1/2'}>
              <SelectInput
                required={'This field is required'}
                name={'source_type'}
                label={'Source Type'}
                control={control}
                options={[
                  { label: 'Customers list', value: 1 },
                  { label: 'Sold report', value: 2 },
                ]}
              />
            </div>
          </div>
          <div className={'flex flex-1 flex-col pt-8 relative'}>
            <div className={'flex flex-row justify-between items-center pb-3'}>
              <div className={'flex flex-col'}>
                <span className={'font-bold text-base'}>Match rows</span>
                <span className={'mt-2 font-medium text-sm'}>
                  For a precise upload, please match the table headers below
                  {data.data?.length > 200 && (
                    <span className={'ml-2 font-normal text-xs'}>
                      (Showing first 200 rows)
                    </span>
                  )}
                </span>
              </div>
              <div>
                <GhostButton
                  transparent={true}
                  onClick={(e) => {
                    e.preventDefault()
                    exportToCSV(
                      [(databaseColumns ?? [])?.map((column) => startCase(column))],
                      'dl_saas_template.csv',
                      true,
                      'csv',
                    )
                  }}>
                  Download CSV Template
                </GhostButton>
              </div>
            </div>
            {errors.email && (
              <div className={'pb-3 font-medium text-sm text-secondary-bittersweet-red'}>
                Mapping email address is required.
              </div>
            )}
            <div
              className={
                'flex flex-1 flex-col max-h-[calc(100vh_-_450px)] overflow-auto flex-1'
              }>
              <CustomersUploadColumnsMapper
                data={data.data}
                columns={columns}
                rowsOptions={rowsOptions}
              />
              {!inView && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={'flex flex-1 items-center justify-center'}>
                  Loading...
                </motion.div>
              )}
            </div>
          </div>
        </div>
        <div className={'h-20 items-center flex justify-between border-t-2 py-4'}>
          <Button
            variant={'secondary'}
            disabled={isSubmitting}
            onClick={(e) => {
              e.preventDefault()
              closeModal()
            }}>
            Close
          </Button>
          <Button disabled={isSubmitting} type={'submit'}>
            Import
          </Button>
        </div>
      </form>
    </FormProvider>
  )
}

export default FTPFileMapper
